import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isPrintInstructionsReadOnly, isMobileMode } from '@app/utils/utils';
import { setDarkMode } from '@app/actions/whiteInkOptionsActions';
import { setPrintInstructions, setPrintInstructionsExpanded } from '@app/actions/printInstructionsActions';

import ZoomTool from '@components/zoom-tool/ZoomTool';
import Toggle from '@components/toggle/Toggle';
import UndoRedoTool from './undo-redo-tool/UndoRedoTool';
import ExpandableInput from '@components/expandable-input/ExpandableInput';

import './TopToolPanel.scss';

const TopToolPanel = ({
  artworkPreview,
  artworkPreviewZoom,
  setDarkMode,
  isDarkMode,
  whiteInkOption,
  includesWhiteUnderprint,
  undoRedoProps,
  whiteAdjustmentNeeded,
  printInstructionsNeeded,
  isPrintInstructionsReadOnly,
  printInstructions,
  printInstructionsExpanded,
  setPrintInstructions,
  setPrintInstructionsExpanded,
}) => {
  const { minZoom = 0.05, maxZoom = 5 } = artworkPreview || {};
  const setZoomLevel = (zoom) => artworkPreview?.setZoomLevel(zoom);
  const autoZoom = () => artworkPreview?.setZoomLevel(artworkPreview.calculatePreferredZoom());
  const [darkModeDisabled, setDarkModeDisabled] = useState(true);

  useEffect(() => {
    const isRemoveWhiteOption = ['removeAllWhite', 'removeWhiteBackground', 'selectWhiteToRemove'].includes(whiteInkOption);

    setDarkModeDisabled(!includesWhiteUnderprint);
    setDarkMode(includesWhiteUnderprint && isRemoveWhiteOption);
  }, [includesWhiteUnderprint, whiteInkOption, setDarkMode]);

  const onDarkModeChange = () => {
    setDarkMode(!isDarkMode);
  }

  return (
    <div id="qau-white-options-top-tool-panel" className={classnames({ 'with-print-instructions': printInstructionsNeeded })}>
      {whiteAdjustmentNeeded ? (
      <Toggle
        isToggled={isDarkMode}
        isDisabled={darkModeDisabled}
        onChange={onDarkModeChange}
        ariaLabelLocaleKey="qau-dark-mode-aria-label"
        titleLocaleKey="qau-dark-mode-title"
        stateOnLocaleKey="qau-state-on-label"
        stateOffLocaleKey="qau-state-off-label"
      />
      ) : (
        <div />
      )}
      <ZoomTool
        currentZoom={artworkPreviewZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        setZoomLevel={setZoomLevel}
        autoZoom={autoZoom}
      />
      {whiteInkOption === 'selectWhiteToRemove' &&
        <UndoRedoTool {...undoRedoProps} />
      }
      {printInstructionsNeeded && (
        <div id="qau-print-instructions-container">
          <ExpandableInput
            titleLocKey="qau-print-instructions-title"
            placeholderLocKey="qau-print-instructions-placeholder"
            collapseLocKey="qau-print-instructions-done-btn"
            value={printInstructions}
            expanded={printInstructionsExpanded}
            readOnly={isPrintInstructionsReadOnly}
            onValueChange={setPrintInstructions}
            onExpandedChange={setPrintInstructionsExpanded}
        />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({
    whiteInkOptions: {
      artworkPreview,
      artworkPreviewZoom,
      isDarkMode,
      whiteInkOption,
      includesWhiteUnderprint,
      whiteAdjustmentNeeded,
    },
    printInstructions: { printInstructions, expanded: printInstructionsExpanded },
    matrix,
  },
  { flowConfig, context },
) => {
  const { quote = {} } = context.projectData || {};
  const { specialRequest } = quote;
  const isMobileView = isMobileMode(matrix.ctx);

  return {
    artworkPreview,
    artworkPreviewZoom,
    isDarkMode,
    whiteInkOption,
    includesWhiteUnderprint,
    whiteAdjustmentNeeded,
    printInstructions,
    printInstructionsExpanded,
    printInstructionsNeeded: !isMobileView && specialRequest === 'Y',
    isPrintInstructionsReadOnly: isPrintInstructionsReadOnly(flowConfig),
  };
};

const mapDispatchToProps = {
  setDarkMode,
  setPrintInstructions,
  setPrintInstructionsExpanded,
};

TopToolPanel.propTypes = {
  artworkPreview: PropTypes.object,
  undoRedoProps: PropTypes.object.isRequired,
  artworkPreviewZoom: PropTypes.number.isRequired,
  printInstructionsNeeded: PropTypes.bool.isRequired,
  isPrintInstructionsReadOnly: PropTypes.bool.isRequired,
  printInstructions: PropTypes.string,
  printInstructionsExpanded: PropTypes.bool.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  whiteInkOption: PropTypes.string,
  includesWhiteUnderprint: PropTypes.bool.isRequired,
  whiteAdjustmentNeeded: PropTypes.bool.isRequired,
  setDarkMode: PropTypes.func.isRequired,
  setPrintInstructions: PropTypes.func.isRequired,
  setPrintInstructionsExpanded: PropTypes.func.isRequired,
};

export default inject('flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(TopToolPanel));
